import { Button, Flex, Paragraph, Division } from "@ir/ui";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type PointerEvent,
  type RefObject,
  type SyntheticEvent,
} from "react";
import "./ImageCrop.scss";

const DEFAULT_ASPECT = 1;
const DEFAULT_OUTPUT_SIZE = 200;
const DEFAULT_OUTPUT_FILE_NAME = "cropped-image.jpg";
const DEFAULT_JPEG_QUALITY = 0.92;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.1;
const VIEWPORT_PADDING = 80;

interface NaturalSize {
  width: number;
  height: number;
}

interface CropFrameLayout {
  cropLeft: number;
  cropTop: number;
  imgLeft: number;
  imgTop: number;
  imgDisplayW: number;
  imgDisplayH: number;
}

interface CropFrameLayoutParams {
  naturalSize: NaturalSize;
  viewportWidth: number;
  viewportHeight: number;
  cropWindowWidth: number;
  cropWindowHeight: number;
  panX: number;
  panY: number;
  scale: number;
}

function getCropWindowDimensions(
  outputSize: number,
  aspect: number,
): { cropWindowWidth: number; cropWindowHeight: number } {
  const cropWindowWidth =
    aspect >= 1 ? outputSize : Math.round(outputSize * aspect);
  const cropWindowHeight =
    aspect >= 1 ? Math.round(outputSize / aspect) : outputSize;
  return { cropWindowWidth, cropWindowHeight };
}

function formatAspectLabel(aspect: number): string {
  if (!Number.isFinite(aspect) || aspect <= 0) return "1:1";
  return Number.isInteger(aspect) ? `${aspect}:1` : `${aspect.toFixed(2)}:1`;
}

function computeBaseFit(
  naturalWidth: number,
  naturalHeight: number,
  cropWindowWidth: number,
  cropWindowHeight: number,
): number {
  return Math.max(
    cropWindowWidth / naturalWidth,
    cropWindowHeight / naturalHeight,
  );
}

function getCropFrameLayout(
  params: CropFrameLayoutParams,
): CropFrameLayout | null {
  const {
    naturalSize,
    viewportWidth,
    viewportHeight,
    cropWindowWidth,
    cropWindowHeight,
    panX,
    panY,
    scale,
  } = params;
  const { width: nw, height: nh } = naturalSize;
  if (nw < 1 || nh < 1 || scale <= 0) {
    return null;
  }

  const cropLeft = (viewportWidth - cropWindowWidth) / 2;
  const cropTop = (viewportHeight - cropWindowHeight) / 2;
  const imgDisplayW = nw * scale;
  const imgDisplayH = nh * scale;
  const imgLeft = viewportWidth / 2 + panX - imgDisplayW / 2;
  const imgTop = viewportHeight / 2 + panY - imgDisplayH / 2;

  return { cropLeft, cropTop, imgLeft, imgTop, imgDisplayW, imgDisplayH };
}

/** Renders exactly what appears inside the fixed crop frame (white fill + image). */
async function exportFrameToJpeg(
  image: HTMLImageElement,
  naturalSize: NaturalSize,
  layout: CropFrameLayout,
  cropWindowWidth: number,
  cropWindowHeight: number,
  outputSize: number,
  outputFileName: string,
): Promise<File> {
  const { width: nw, height: nh } = naturalSize;
  const { cropLeft, cropTop, imgLeft, imgTop, imgDisplayW, imgDisplayH } =
    layout;

  const mapX = outputSize / cropWindowWidth;
  const mapY = outputSize / cropWindowHeight;
  const destX = (imgLeft - cropLeft) * mapX;
  const destY = (imgTop - cropTop) * mapY;
  const destW = imgDisplayW * mapX;
  const destH = imgDisplayH * mapY;

  const canvas = document.createElement("canvas");
  canvas.width = outputSize;
  canvas.height = outputSize;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("No 2d context");
  }

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, outputSize, outputSize);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(image, 0, 0, nw, nh, destX, destY, destW, destH);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Failed to export cropped image"));
          return;
        }
        resolve(new File([blob], outputFileName, { type: "image/jpeg" }));
      },
      "image/jpeg",
      DEFAULT_JPEG_QUALITY,
    );
  });
}

export interface ImageCropProps {
  value?: File | null;
  onChange?: (file: File | null) => void;
  onApply?: (file: File) => void;
  onCancelPreview?: () => void;
  accept?: string;
  /**
   * Dynamic crop ratio from the parent. Keep current callers on `1` until the
   * exporter supports non-square output safely.
   */
  aspect?: number;
  /** Dynamic square output size in pixels. */
  outputSize?: number;
  /** Dynamic base filename for the generated JPG. */
  outputFileName?: string;
  helperText?: string;
}

interface ImageCropEditorBodyProps {
  draftSrc: string | null;
  hasSavedPreview: boolean;
  valuePreviewSrc: string | null;
  helperText: string | undefined;
  cropWindowWidth: number;
  cropWindowHeight: number;
  outputSize: number;
  naturalSize: NaturalSize | null;
  zoom: number;
  isDragging: boolean;
  isApplying: boolean;
  canApply: boolean;
  viewportMinHeight: number;
  scale: number;
  panX: number;
  panY: number;
  viewportRef: RefObject<HTMLDivElement | null>;
  imgRef: RefObject<HTMLImageElement | null>;
  onImageLoad: (e: SyntheticEvent<HTMLImageElement>) => void;
  onPointerDown: (event: PointerEvent<HTMLDivElement>) => void;
  onPointerMove: (event: PointerEvent<HTMLDivElement>) => void;
  onPointerEnd: (event: PointerEvent<HTMLDivElement>) => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onCancel: () => void;
  onApply: () => void;
}

function ImageCropEditorBody({
  draftSrc,
  hasSavedPreview,
  valuePreviewSrc,
  helperText,
  cropWindowWidth,
  cropWindowHeight,
  outputSize,
  naturalSize,
  zoom,
  isDragging,
  isApplying,
  canApply,
  viewportMinHeight,
  scale,
  panX,
  panY,
  viewportRef,
  imgRef,
  onImageLoad,
  onPointerDown,
  onPointerMove,
  onPointerEnd,
  onZoomIn,
  onZoomOut,
  onCancel,
  onApply,
}: Readonly<ImageCropEditorBodyProps>) {
  if (draftSrc) {
    const helper =
      helperText ??
      `Drag to reposition. Zoom changes only the Image behind the ${cropWindowWidth}×${cropWindowHeight} crop window. Output is ${outputSize}×${outputSize} JPG.`;

    return (
      <>
        <Paragraph className="text-grey-600 font-regular m-0 image-crop-helper pb-2">
          {helper}
        </Paragraph>

        <Flex
          align="center"
          justifyContent="space-between"
          gap={0.75}
          wrap="wrap"
          className="image-crop-zoom-bar"
        >
          <Paragraph className="text-grey-600 font-regular m-0">
            Zoom image
          </Paragraph>
          <Flex align="center" gap={0.5} wrap="wrap">
            <Button
              size="small"
              color="grey"
              onPress={onZoomOut}
              isDisabled={!naturalSize || zoom <= ZOOM_MIN}
              aria-label="Zoom out"
            >
              -
            </Button>
            <span className="image-crop-zoom-value" aria-live="polite">
              {Math.round(zoom * 100)}%
            </span>
            <Button
              size="small"
              color="grey"
              onPress={onZoomIn}
              isDisabled={!naturalSize || zoom >= ZOOM_MAX}
              aria-label="Zoom in"
            >
              +
            </Button>
          </Flex>
        </Flex>

        <Division
          ref={viewportRef}
          className="image-crop-viewport"
          style={{ minHeight: viewportMinHeight }}
        >
          <Division
            className="image-crop-frame-fill"
            style={{
              width: cropWindowWidth,
              height: cropWindowHeight,
            }}
            aria-hidden
          />

          <Division
            className={`image-crop-stage${isDragging ? " image-crop-stage--dragging" : ""}`}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerEnd}
            onPointerCancel={onPointerEnd}
          >
            <img
              ref={imgRef}
              alt="Crop preview"
              src={draftSrc}
              className={`image-crop-image${naturalSize ? "" : " image-crop-image--loading"}`}
              draggable={false}
              style={
                naturalSize
                  ? {
                      width: naturalSize.width * scale,
                      height: naturalSize.height * scale,
                      transform: `translate(calc(-50% + ${panX}px), calc(-50% + ${panY}px))`,
                    }
                  : undefined
              }
              onLoad={onImageLoad}
            />
          </Division>

          <Division
            className="image-crop-frame"
            style={{
              width: cropWindowWidth,
              height: cropWindowHeight,
            }}
            aria-hidden
          />
        </Division>

        <Flex
          justifyContent="space-between"
          gap={0.5}
          className="image-crop-actions"
        >
          <Button
            color="grey"
            size="small"
            onPress={onCancel}
            isDisabled={isApplying}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            size="small"
            onPress={onApply}
            isLoading={isApplying}
            isDisabled={isApplying || !canApply}
          >
            Apply Crop
          </Button>
        </Flex>
      </>
    );
  }

  if (hasSavedPreview && valuePreviewSrc) {
    return (
      <>
        <Paragraph className="text-grey-600 font-regular m-0 image-crop-helper pb-2">
          Preview
        </Paragraph>
        <Division className="image-crop-canvas image-crop-canvas-stage">
          <Division className="image-crop-preview-card image-crop-preview-card-stage">
            <img
              src={valuePreviewSrc}
              alt="Selected crop"
              className="image-crop-preview-image"
            />
          </Division>
        </Division>
      </>
    );
  }

  return (
    <>
      <Paragraph className="text-grey-600 font-regular m-0 image-crop-helper">
        Preview
      </Paragraph>
      <Division className="image-crop-canvas image-crop-canvas-stage">
        <Division className="image-crop-empty-state image-crop-empty-state-stage">
          <Paragraph className="text-grey-500 font-regular m-0">
            Choose an image to open the crop editor and preview it here.
          </Paragraph>
        </Division>
      </Division>
    </>
  );
}

/**
 * Browse + crop with a fixed on-screen crop window. Only the image moves and
 * zooms behind the frame; export maps the frame to the configured output size.
 */
export const ImageCrop = ({
  value,
  onChange,
  onApply,
  onCancelPreview,
  accept = "image/*",
  aspect = DEFAULT_ASPECT,
  outputSize = DEFAULT_OUTPUT_SIZE,
  outputFileName = DEFAULT_OUTPUT_FILE_NAME,
  helperText,
}: Readonly<ImageCropProps>) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const dragOriginRef = useRef<{ x: number; y: number } | null>(null);
  const panOriginRef = useRef({ x: 0, y: 0 });

  const [isApplying, setIsApplying] = useState(false);
  const [draftSrc, setDraftSrc] = useState<string | null>(null);
  const [draftFileName, setDraftFileName] = useState("");
  const [valuePreviewSrc, setValuePreviewSrc] = useState<string | null>(null);
  const [naturalSize, setNaturalSize] = useState<NaturalSize | null>(null);
  const [baseFit, setBaseFit] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });

  const { cropWindowWidth, cropWindowHeight } = getCropWindowDimensions(
    outputSize,
    aspect,
  );
  const scale = baseFit * zoom;
  const viewportMinHeight =
    Math.max(cropWindowWidth, cropWindowHeight) + VIEWPORT_PADDING;

  const resetTransform = useCallback(() => {
    setNaturalSize(null);
    setBaseFit(1);
    setZoom(1);
    setPanX(0);
    setPanY(0);
    dragOriginRef.current = null;
    panOriginRef.current = { x: 0, y: 0 };
  }, []);

  const clearDraft = useCallback(() => {
    setDraftSrc((prev) => {
      if (prev) {
        URL.revokeObjectURL(prev);
      }
      return null;
    });
    setDraftFileName("");
    resetTransform();
  }, [resetTransform]);

  useEffect(() => {
    return () => {
      if (draftSrc) {
        URL.revokeObjectURL(draftSrc);
      }
    };
  }, [draftSrc]);

  useEffect(() => {
    if (!(value instanceof File)) {
      setValuePreviewSrc(null);
      return;
    }

    const nextPreviewSrc = URL.createObjectURL(value);
    setValuePreviewSrc(nextPreviewSrc);

    return () => {
      URL.revokeObjectURL(nextPreviewSrc);
    };
  }, [value]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el || !draftSrc) {
      return;
    }
    const update = () => {
      setViewportSize({
        width: el.clientWidth,
        height: el.clientHeight,
      });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, [draftSrc, cropWindowWidth, cropWindowHeight]);

  const onImageLoad = useCallback(
    (e: SyntheticEvent<HTMLImageElement>) => {
      const nw = e.currentTarget.naturalWidth;
      const nh = e.currentTarget.naturalHeight;
      if (nw < 1 || nh < 1) {
        return;
      }
      const fit = computeBaseFit(nw, nh, cropWindowWidth, cropWindowHeight);
      setNaturalSize({ width: nw, height: nh });
      setBaseFit(fit);
      setZoom(1);
      setPanX(0);
      setPanY(0);
      panOriginRef.current = { x: 0, y: 0 };
    },
    [cropWindowWidth, cropWindowHeight],
  );

  const applyZoom = useCallback((nextZoom: number) => {
    setZoom((prevZoom) => {
      const ratio = nextZoom / prevZoom;
      setPanX((x) => x * ratio);
      setPanY((y) => y * ratio);
      panOriginRef.current = {
        x: panOriginRef.current.x * ratio,
        y: panOriginRef.current.y * ratio,
      };
      return nextZoom;
    });
  }, []);

  const handleZoomIn = useCallback(() => {
    applyZoom(Math.min(ZOOM_MAX, Math.round((zoom + ZOOM_STEP) * 100) / 100));
  }, [applyZoom, zoom]);

  const handleZoomOut = useCallback(() => {
    applyZoom(Math.max(ZOOM_MIN, Math.round((zoom - ZOOM_STEP) * 100) / 100));
  }, [applyZoom, zoom]);

  const handlePointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (!naturalSize) {
        return;
      }
      event.currentTarget.setPointerCapture(event.pointerId);
      dragOriginRef.current = { x: event.clientX, y: event.clientY };
      panOriginRef.current = { x: panX, y: panY };
      setIsDragging(true);
    },
    [naturalSize, panX, panY],
  );

  const handlePointerMove = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (!dragOriginRef.current) {
        return;
      }
      const dx = event.clientX - dragOriginRef.current.x;
      const dy = event.clientY - dragOriginRef.current.y;
      setPanX(panOriginRef.current.x + dx);
      setPanY(panOriginRef.current.y + dy);
    },
    [],
  );

  const endDrag = useCallback((event: PointerEvent<HTMLDivElement>) => {
    if (dragOriginRef.current) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragOriginRef.current = null;
    setIsDragging(false);
  }, []);

  const handleSelectFile = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0] ?? null;
      event.target.value = "";
      if (!file) return;

      resetTransform();
      setDraftSrc((prev) => {
        if (prev) {
          URL.revokeObjectURL(prev);
        }
        return URL.createObjectURL(file);
      });
      setDraftFileName(file.name);
    },
    [resetTransform],
  );

  const handleApply = useCallback(async () => {
    const image = imgRef.current;
    const viewport = viewportRef.current;
    if (!image || !naturalSize || !viewport) {
      return;
    }

    const layout = getCropFrameLayout({
      naturalSize,
      viewportWidth: viewport.clientWidth,
      viewportHeight: viewport.clientHeight,
      cropWindowWidth,
      cropWindowHeight,
      panX,
      panY,
      scale,
    });
    if (!layout) {
      return;
    }

    setIsApplying(true);
    try {
      const baseName = (draftFileName || outputFileName)
        .replace(/\.[^.]+$/, "")
        .replace(/\.(jpe?g)$/i, "");
      const nextFile = await exportFrameToJpeg(
        image,
        naturalSize,
        layout,
        cropWindowWidth,
        cropWindowHeight,
        outputSize,
        `${baseName}.jpg`,
      );
      onChange?.(nextFile);
      onApply?.(nextFile);
      clearDraft();
    } finally {
      setIsApplying(false);
    }
  }, [
    clearDraft,
    cropWindowWidth,
    cropWindowHeight,
    draftFileName,
    naturalSize,
    onApply,
    onChange,
    outputFileName,
    outputSize,
    panX,
    panY,
    scale,
  ]);

  const handleCancel = useCallback(() => {
    clearDraft();
    onCancelPreview?.();
  }, [clearDraft, onCancelPreview]);

  const handleOpenFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const aspectLabel = formatAspectLabel(aspect);
  const selectedFileName = draftFileName || value?.name || "";
  const hasSavedPreview = Boolean(valuePreviewSrc && !draftSrc);
  const canApply = Boolean(
    draftSrc &&
    naturalSize &&
    viewportSize.width > 0 &&
    viewportSize.height > 0,
  );

  return (
    <Flex direction="column" gap={1} className="image-crop">
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleSelectFile}
        className="image-crop-input"
      />

      <Division className="image-crop-layout">
        <Division className="image-crop-panel">
          <Flex
            align="start"
            justifyContent="space-between"
            gap={1}
            wrap="wrap"
            className="image-crop-panel-header"
          >
            <Flex direction="column" gap={0.25}>
              <Paragraph className="text-grey-700 font-bold m-0">
                Image Upload
              </Paragraph>
            </Flex>
            <Button size="small" color="primary" onPress={handleOpenFilePicker}>
              {selectedFileName ? "Change Image" : "Choose Image"}
            </Button>
          </Flex>

          <Flex
            align="center"
            justifyContent="space-between"
            gap={0.75}
            wrap="wrap"
            className="image-crop-meta"
          >
            <Flex direction="column" gap={0.25}>
              <Paragraph className="text-grey-700 font-bold m-0">
                {selectedFileName || "No image selected"}
              </Paragraph>
              <Paragraph className="text-grey-500 font-regular m-0">
                {selectedFileName
                  ? "You can replace this file anytime before saving."
                  : "Choose an image to open the crop editor."}
              </Paragraph>
            </Flex>
            <Flex gap={0.5} wrap="wrap" className="image-crop-badges">
              <span className="image-crop-badge">
                {outputSize}x{outputSize} JPG
              </span>
              <span className="image-crop-badge">
                Aspect Ratio {aspectLabel}
              </span>
            </Flex>
          </Flex>
        </Division>

        <Division className="image-crop-editor">
          <ImageCropEditorBody
            draftSrc={draftSrc}
            hasSavedPreview={hasSavedPreview}
            valuePreviewSrc={valuePreviewSrc}
            helperText={helperText}
            cropWindowWidth={cropWindowWidth}
            cropWindowHeight={cropWindowHeight}
            outputSize={outputSize}
            naturalSize={naturalSize}
            zoom={zoom}
            isDragging={isDragging}
            isApplying={isApplying}
            canApply={canApply}
            viewportMinHeight={viewportMinHeight}
            scale={scale}
            panX={panX}
            panY={panY}
            viewportRef={viewportRef}
            imgRef={imgRef}
            onImageLoad={onImageLoad}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerEnd={endDrag}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onCancel={handleCancel}
            onApply={handleApply}
          />
        </Division>
      </Division>
    </Flex>
  );
};
